import { useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  CallEnd as CallEndIcon,
  SignalCellularAlt as SignalIcon,
} from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { useLessonCall } from '@/webrtc/callContext'
import { CallParticipantAvatar } from './CallParticipantAvatar'
import { CallVideoGrid } from './CallVideoGrid'

function formatDuration(seconds: number): string {
  const hh = Math.floor(seconds / 3600)
  const mm = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const ss = (seconds % 60).toString().padStart(2, '0')

  // Уроки бывают длиннее часа — без этой ветки таймер показал бы «61:05».
  return hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`
}

/**
 * Панель идущего звонка на странице урока: участники с индикацией голоса,
 * микрофон, камера, демонстрация экрана, качество связи и выход.
 */
export function LessonCallWidget() {
  const { t } = useTranslation()
  const call = useLessonCall()
  const audioContainerRef = useRef<HTMLDivElement | null>(null)
  const audioElementsRef = useRef<Map<number, HTMLAudioElement>>(new Map())

  /**
   * Звук удалённых участников: <audio> создаются императивно, чтобы
   * переприсваивание srcObject не зависело от перерисовок React.
   */
  useEffect(() => {
    const container = audioContainerRef.current
    if (!container) return

    const elements = audioElementsRef.current

    Object.entries(call.remoteStreams).forEach(([rawId, stream]) => {
      const userId = Number(rawId)
      let element = elements.get(userId)

      if (!element) {
        element = document.createElement('audio')
        element.autoplay = true
        elements.set(userId, element)
        container.appendChild(element)
      }

      if (element.srcObject !== stream) {
        element.srcObject = stream
        void element.play().catch(() => undefined)
      }
    })

    elements.forEach((element, userId) => {
      if (call.remoteStreams[userId]) return
      element.srcObject = null
      element.remove()
      elements.delete(userId)
    })
  }, [call.remoteStreams])

  useEffect(() => {
    const elements = audioElementsRef.current
    return () => {
      elements.forEach((element) => {
        element.srcObject = null
        element.remove()
      })
      elements.clear()
    }
  }, [])

  if (call.status !== 'connecting' && call.status !== 'active' && call.status !== 'ending') {
    return null
  }

  const isVideo = call.session?.type === 'video'
  const qualityLabel =
    call.quality === 0
      ? t('lessonCall.quality.poor')
      : call.quality === 1
        ? t('lessonCall.quality.fair')
        : t('lessonCall.quality.good')

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6">
            {isVideo ? t('lessonCall.titleVideo') : t('lessonCall.titleAudio')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {call.quality !== null && (
              <Tooltip title={qualityLabel}>
                <SignalIcon
                  fontSize="small"
                  color={call.quality === 0 ? 'error' : call.quality === 1 ? 'warning' : 'success'}
                />
              </Tooltip>
            )}
            <Chip
              size="small"
              label={
                call.status === 'connecting'
                  ? t('lessonCall.status.connecting')
                  : formatDuration(call.duration)
              }
            />
          </Box>
        </Box>

        {call.error && (
          <Alert
            severity="error"
            sx={{ mb: 1.5 }}
            onClose={call.clearError}
            action={
              call.errorCode === 'subscription_parameter_denied' ||
              call.errorCode === 'subscription_limit_exceeded' ? (
                <Button
                  size="small"
                  color="inherit"
                  component={RouterLink}
                  to={ROUTES.PROFILE_SUBSCRIPTION}
                >
                  {t('subscription.goToSubscription')}
                </Button>
              ) : undefined
            }
          >
            {call.error}
          </Alert>
        )}

        {/* Сетка сама возвращает null без видеодорожек: камеру можно включить
            и в аудио-звонке, и тогда картинка должна появиться у всех. */}
        <CallVideoGrid />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {call.participants.map((participant) => (
            <CallParticipantAvatar key={participant.userId} participant={participant} />
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Tooltip title={call.micEnabled ? t('lessonCall.actions.mute') : t('lessonCall.actions.unmute')}>
            <IconButton onClick={call.toggleMic} color={call.micEnabled ? 'primary' : 'default'}>
              {call.micEnabled ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
          </Tooltip>

          {call.config?.video.enabled && (
            <Tooltip
              title={
                call.cameraEnabled ? t('lessonCall.actions.cameraOff') : t('lessonCall.actions.cameraOn')
              }
            >
              <IconButton
                onClick={() => void call.toggleCamera()}
                color={call.cameraEnabled ? 'primary' : 'default'}
              >
                {call.cameraEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
              </IconButton>
            </Tooltip>
          )}

          {call.config?.screen_share_enabled && (
            <Tooltip
              title={
                call.screenSharing
                  ? t('lessonCall.actions.screenShareStop')
                  : t('lessonCall.actions.screenShareStart')
              }
            >
              <IconButton
                onClick={() => void call.toggleScreenShare()}
                color={call.screenSharing ? 'primary' : 'default'}
              >
                {call.screenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            color="error"
            startIcon={<CallEndIcon />}
            onClick={() => void call.endCall()}
            disabled={call.status === 'ending'}
          >
            {t('lessonCall.actions.leave')}
          </Button>
        </Box>

        <Box ref={audioContainerRef} sx={{ display: 'none' }} />
      </CardContent>
    </Card>
  )
}
